import { ImisResponseResult, QueryDefinitionItem } from "../models/imis.models";
import { RestUtility } from "./rest.utility";

/**
 * Static helpers for looking up iMIS query definitions, managing their REST-access flag.
 */
export class QueriesUtility {

    /**
     * Looks up a query definition by its path using the FindByPath query-definition operation.
     * @param queryPath The path of the query definition to look up.
     * @returns The matching query definition, or null if none was found.
     */
    public static async getQueryDefinitionByPath(queryPath: string) {
        let queryDefinition: QueryDefinitionItem = null;

        let queryDefinitionResponse = await RestUtility.restApiSendData<ImisResponseResult<QueryDefinitionItem>>("/api/querydefinition/_execute", {
            "$type": "Asi.Soa.Core.DataContracts.GenericExecuteRequest, Asi.Contracts",
            "OperationName": "FindByPath",
            "Parameters": [queryPath]
        });
        if (queryDefinitionResponse?.Result) {
            queryDefinition = queryDefinitionResponse.Result;
        }

        // ensuring query definition is returned if this is indeed a non-cloud instance response
        if (queryDefinition == null && queryDefinitionResponse?.Result === undefined) {
            queryDefinition = <any>queryDefinitionResponse;
        }

        return queryDefinition;
    }

    /**
     * Retrieves whether REST access is currently enabled for the query definition at the given path.
     * @param queryPath The path of the query definition to check.
     * @returns True or false if the REST access status could be determined, or null if the response could not be parsed.
     */
    public static async getQueryRestAccessByPath(queryPath: string) {
        let queryRestAccess: boolean = null;

        let queryDefinitionResponse = await RestUtility.restApiSendData<ImisResponseResult<any>>("/api/querydefinition/_execute", {
            "$type": "Asi.Soa.Core.DataContracts.GenericExecuteRequest, Asi.Contracts",
            "OperationName": "GetRestAccess",
            "Parameters": {
                "$type": "System.Collections.ObjectModel.Collection`1[[System.Object, mscorlib]], mscorlib",
                "$values": [
                    {
                        "$type": "System.String[]",
                        "$values": [queryPath]
                    }
                ]
            }
        }, "POST", true);
        if (queryDefinitionResponse?.Result != null) {
            let resultValue = queryDefinitionResponse.Result.$values[0];
            let regex = /'(.+?)' REST access set to '(.*?)'/g;
            let match = regex.exec(resultValue);
            if (match && match.length >= 3) {
                queryRestAccess = match[2].toLowerCase() === "true";
            }
        }

        return queryRestAccess;
    }

    /**
     * Retrieves the REST access status for multiple query definitions at once.
     * @param queryPaths The paths of the query definitions to check.
     * @returns An object mapping each query path to its REST access status, or null if the response could not be parsed.
     */
    public static async getQueryRestAccessByPaths(queryPaths: string[]) {
        let queryRestAccess: { [key: string]: boolean } = null;

        let queryDefinitionResponse = await RestUtility.restApiSendData<ImisResponseResult<any>>("/api/querydefinition/_execute", {
            "$type": "Asi.Soa.Core.DataContracts.GenericExecuteRequest, Asi.Contracts",
            "OperationName": "GetRestAccess",
            "Parameters": {
                "$type": "System.Collections.ObjectModel.Collection`1[[System.Object, mscorlib]], mscorlib",
                "$values": [
                    {
                        "$type": "System.String[]",
                        "$values": queryPaths
                    }
                ]
            }
        }, "POST", true);
        if (queryDefinitionResponse?.Result != null) {
            queryRestAccess = {};
            queryDefinitionResponse.Result.$values.forEach(resultValue => {
                let regex = /'(.+?)' REST access set to '(.*?)'/g;
                let match = regex.exec(resultValue);
                if (match && match.length >= 3) {
                    queryRestAccess[match[1]] = match[2].toLowerCase() === "true";
                } 
            });
        }

        return queryRestAccess;
    }

    /**
     * Enables or disables REST access on the query definition at the given path.
     * @param queryPath The path of the query definition to update.
     * @param restAccess True to enable REST access, false to disable it.
     * @returns True if the update succeeded, false otherwise.
     */
    public static async setQueryRestAccessByPath(queryPath: string, restAccess: boolean) {
        let isSuccess = false;

        let queryDefinitionResponse = await RestUtility.restApiSendData<ImisResponseResult<any>>("/api/querydefinition/_execute", {
            "$type": "Asi.Soa.Core.DataContracts.GenericExecuteRequest, Asi.Contracts",
            "OperationName": "SetRestAccess",
            "Parameters": {
                "$type": "System.Collections.ObjectModel.Collection`1[[System.Object, mscorlib]], mscorlib",
                "$values": [
                    {
                        "$type": "System.String[]",
                        "$values": [queryPath]
                    },
                    {
                        "$type": "System.Boolean",
                        "$value": restAccess
                    }
                ]
            }
        }, "POST", true);
        if (queryDefinitionResponse?.Result != null) {
            let resultValue = queryDefinitionResponse.Result.$values[0];
            let regex = /'(.+?)' REST access set to '(.*?)'/g;
            let match = regex.exec(resultValue);
            if (match && match.length >= 3) {
                isSuccess = true;
            }
        }

        return isSuccess;
    }
}
